import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FeatureSwitch, FeatureSwitchUpdateRequest } from '../../_models/admin/feature-switch.models';
import { MaintenanceRole, UserListItem } from '../../_models/admin/user-maintenance.models';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { UserMaintenanceService } from '../../_services/user-maintenance/user-maintenance.service';
import { AccessCheckService } from '../../_services/access-check/access-check.service';
import { AccessCheckUserSuggestion } from '../../_models/admin/access-check.models';
import { TypeaheadComponent } from '../../shared/typeahead/typeahead.component';

/** What the page hands the dialog: the switch as it stands, and the choices for roles and brands. */
export interface FeatureSwitchEditData {
  item: FeatureSwitch;
  roles: MaintenanceRole[];
  brands: BrandInfo[];
  /**
   * featureswitches.lifecycle is on: an adopted switch's targeting is read-only, and a linked switch's role
   * picker offers only the roles that hold its feature. Missing or false is the dialog as it was.
   */
  lifecycle?: boolean;
  /**
   * search.autocomplete.feature-switch-users is on: the named-user search box suggests matching accounts, and picking
   * one searches for it. Missing or false is a plain box.
   */
  userAutocomplete?: boolean;
}

/** A picked user: the id is what is saved, the name is what is shown. */
export interface PickedUser {
  id: number;
  name: string;
}

/** `yyyy-MM-ddTHH:mm` in the viewer's local time, for a datetime-local input. */
export function toLocalInput(iso: string | null): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
}

/** A datetime-local value (the viewer's local time) as a UTC ISO string, or null when blank. */
export function fromLocalInput(value: string): string | null {
  if (!value) {
    return null;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Edits every setting of one feature switch: the master toggle, rollout %, roles, brands, include and
 * exclude users, and the start/end window. Closes with the full FeatureSwitchUpdateRequest to save, or
 * with nothing when cancelled — the page does the saving, so a failed save shows where every other
 * error on the page does.
 */
@Component({
  selector: 'feature-switch-edit-dialog',
  standalone: true,
  imports: [FormsModule, TypeaheadComponent],
  templateUrl: 'feature-switch-edit-dialog.component.html'
})
export class FeatureSwitchEditDialogComponent implements OnInit {

  public isEnabled: boolean;
  public rolloutPercent: number;
  public selectedRoles = new Set<string>();
  public selectedBrands = new Set<number>();
  public includeUsers: PickedUser[] = [];
  public excludeUsers: PickedUser[] = [];
  public startLocal = '';
  public endLocal = '';

  public searchTerm = '';
  public searching = false;
  public searchResults: UserListItem[] = [];
  public searchError: string = null;

  /** The search box's suggestions (api/Maintenance/AccessCheck/Users; a 404 while admin.access-check is off is none). */
  public readonly searchUserSuggestions = (q: string) => this.accessCheck.searchUsers(q);
  public readonly suggestionUserName = (s: AccessCheckUserSuggestion): string => s?.userName ?? '';
  public readonly suggestionDetail = (s: AccessCheckUserSuggestion): string => [s?.name, s?.email].filter(x => !!x).join(' · ');
  /** A pick puts the email in the box (the user search matches first name, last name or email), else the user name. */
  public readonly suggestionSearchText = (s: AccessCheckUserSuggestion): string => s?.email || s?.userName || '';

  public errors: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FeatureSwitchEditData,
    private dialogRef: MatDialogRef<FeatureSwitchEditDialogComponent, FeatureSwitchUpdateRequest>,
    private userMaintenance: UserMaintenanceService,
    @Optional() private accessCheck: AccessCheckService = null
  ) { }

  ngOnInit(): void {
    const item = this.data.item;
    this.isEnabled = !!item.isEnabled;
    this.rolloutPercent = item.rolloutPercent == null ? 100 : item.rolloutPercent;
    (item.targetRoles || []).forEach(r => this.selectedRoles.add(r.toUpperCase()));
    (item.targetBrandIds || []).forEach(b => this.selectedBrands.add(b));
    this.startLocal = toLocalInput(item.startUtc);
    this.endLocal = toLocalInput(item.endUtc);

    this.includeUsers = (item.includeUserIds || []).map(id => ({ id, name: 'User #' + id }));
    this.excludeUsers = (item.excludeUserIds || []).map(id => ({ id, name: 'User #' + id }));
    this.resolveNames([...this.includeUsers, ...this.excludeUsers]);
  }

  /**
   * The roles the picker offers. For a linked switch while the lifecycle is on, TargetRoles may only narrow the
   * grant, so only the roles holding the feature are offered, plus any already ticked that do not (so they can
   * be unticked: the API refuses to save them).
   */
  public get roles(): MaintenanceRole[] {
    const all = this.data.roles || [];
    if (!this.narrowed) {
      return all;
    }
    const granted = new Set((this.data.item.grantedRoles || []).map(r => r.toUpperCase()));
    return all.filter(r => granted.has(r.code.toUpperCase()) || this.selectedRoles.has(r.code.toUpperCase()));
  }

  /** An adopted switch: access is managed on Role Features, so the targeting fields are read-only. */
  public get adopted(): boolean {
    return !!this.data.lifecycle && this.data.item.stage === 'Adopted';
  }

  /** A switch that gates a role feature, with the lifecycle on: its role picker is narrowed to the grant. */
  public get narrowed(): boolean {
    return !!this.data.lifecycle && this.linked;
  }

  /** A ticked role that does not hold the linked feature. */
  public notGranted(code: string): boolean {
    return this.narrowed && !(this.data.item.grantedRoles || []).some(r => r.toUpperCase() === code.toUpperCase());
  }

  private get linked(): boolean {
    return this.data.item.featureId != null || !!this.data.item.featureName;
  }

  public get brands(): BrandInfo[] {
    return this.data.brands || [];
  }

  public toggleRole(code: string): void {
    const key = code.toUpperCase();
    this.selectedRoles.has(key) ? this.selectedRoles.delete(key) : this.selectedRoles.add(key);
  }

  public toggleBrand(id: number): void {
    this.selectedBrands.has(id) ? this.selectedBrands.delete(id) : this.selectedBrands.add(id);
  }

  public search(): void {
    const term = (this.searchTerm || '').trim();
    if (!term) {
      this.searchResults = [];
      return;
    }
    this.searching = true;
    this.searchError = null;
    this.userMaintenance.searchUsers({ pageNumber: 1, pageSize: 10, searchParam: term }).subscribe({
      next: page => {
        this.searching = false;
        this.searchResults = (page && page.items) || [];
      },
      error: error => {
        this.searching = false;
        this.searchResults = [];
        this.searchError = (error && error.message) || 'Could not search users.';
      }
    });
  }

  public userName(u: UserListItem): string {
    const name = [u.firstName, u.lastName].filter(x => !!x).join(' ');
    return name ? name + (u.email ? ' (' + u.email + ')' : '') : (u.email || 'User #' + u.id);
  }

  /** Adding a user to one list takes them off the other — a user is included or excluded, not both. */
  public include(u: UserListItem): void {
    this.excludeUsers = this.excludeUsers.filter(x => x.id !== u.id);
    if (!this.includeUsers.some(x => x.id === u.id)) {
      this.includeUsers = [...this.includeUsers, { id: u.id, name: this.userName(u) }];
    }
  }

  public exclude(u: UserListItem): void {
    this.includeUsers = this.includeUsers.filter(x => x.id !== u.id);
    if (!this.excludeUsers.some(x => x.id === u.id)) {
      this.excludeUsers = [...this.excludeUsers, { id: u.id, name: this.userName(u) }];
    }
  }

  public removeInclude(id: number): void {
    this.includeUsers = this.includeUsers.filter(x => x.id !== id);
  }

  public removeExclude(id: number): void {
    this.excludeUsers = this.excludeUsers.filter(x => x.id !== id);
  }

  /** The form as the PUT body. */
  public toRequest(): FeatureSwitchUpdateRequest {
    return {
      isEnabled: !!this.isEnabled,
      rolloutPercent: Number(this.rolloutPercent),
      targetRoles: Array.from(this.selectedRoles),
      targetBrandIds: Array.from(this.selectedBrands).sort((a, b) => a - b),
      includeUserIds: this.includeUsers.map(u => u.id),
      excludeUserIds: this.excludeUsers.map(u => u.id),
      startUtc: fromLocalInput(this.startLocal),
      endUtc: fromLocalInput(this.endLocal)
    };
  }

  /** The same checks the API makes that the form can make itself; the API remains the authority. */
  public validate(request: FeatureSwitchUpdateRequest): string[] {
    const errors: string[] = [];
    const pct = request.rolloutPercent;
    if (pct == null || isNaN(pct) || !Number.isInteger(pct) || pct < 0 || pct > 100) {
      errors.push('Rollout must be a whole number from 0 to 100.');
    }
    if (this.startLocal && !request.startUtc) {
      errors.push('The start is not a valid date and time.');
    }
    if (this.endLocal && !request.endUtc) {
      errors.push('The end is not a valid date and time.');
    }
    if (request.startUtc && request.endUtc && new Date(request.startUtc) >= new Date(request.endUtc)) {
      errors.push('The start must be before the end.');
    }
    return errors;
  }

  public save(): void {
    const request = this.toRequest();
    this.errors = this.validate(request);
    if (this.errors.length === 0) {
      this.dialogRef.close(request);
    }
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  /** Stored ids carry no names; look each one up so the lists show people, not numbers. */
  private resolveNames(picked: PickedUser[]): void {
    const ids = Array.from(new Set(picked.map(p => p.id)));
    if (ids.length === 0) {
      return;
    }
    forkJoin(ids.map(id => this.userMaintenance.getUserDetail(id).pipe(
      map(d => ({ id, name: d ? ([d.firstName, d.lastName].filter(x => !!x).join(' ') || d.email || 'User #' + id) : 'User #' + id })),
      catchError(() => of({ id, name: 'User #' + id }))
    ))).subscribe(named => {
      const byId = new Map(named.map(n => [n.id, n.name]));
      this.includeUsers = this.includeUsers.map(u => ({ id: u.id, name: byId.get(u.id) || u.name }));
      this.excludeUsers = this.excludeUsers.map(u => ({ id: u.id, name: byId.get(u.id) || u.name }));
    });
  }
}
